import { IQueryBuilderInstance } from '../interfaces';
import { WhereOperators, WhereTypes } from '../utils/types';

export class QueryBuilder {

    /**
     * Query Builder instance
     */
    protected query: IQueryBuilderInstance;

    public constructor() {
        this.reset();
    }

    /**
     * Add where statement
     *
     * @param where
     */
    public where(where: Array<{ attribute: string, operator: string, value: any }> | Function);
    public where(attribute: string, value: any);
    public where(attribute: string, operator: string, value: any);
    public where(...args: any[]) {
        this.parseWhereArguments(true, args);
    }

    /**
     * Add or where statement
     *
     * @param where
     */
    public orWhere(where: Array<{ attribute: string, operator: string, value: any }> | Function);
    public orWhere(attribute: string, value: any);
    public orWhere(attribute: string, operator: string, value: any);
    public orWhere(...args: any[]) {
        this.parseWhereArguments(false, args);
    }

    /**
     * Add where in statement
     *
     * @param attribute
     * @param values
     */
    public whereIn(attribute: string, values: Array<string | number>) {
        this.parseWhereArguments(true, [attribute, 'IN', values]);
    }

    /**
     * Add where not in statement
     *
     * @param attribute
     * @param values
     */
    public whereNotIn(attribute: string, values: Array<string | number>) {
        this.parseWhereArguments(true, [attribute, 'NOT IN', values]);
    }

    /**
     * Add where between statement
     *
     * @param attribute
     * @param start
     * @param end
     */
    public whereBetween(attribute: string, start: number | string, end: number | string) {
        this.parseWhereArguments(true, [attribute, 'BETWEEN', [start, end]]);
    }

    /**
     * Add where null statement
     *
     * @param attribute
     */
    public whereNull(attribute: string) {
        this.parseWhereArguments(true, [attribute, 'IS NULL', null]);
    }

    /**
     * Add where not null statement
     *
     * @param attribute
     */
    public whereNotNull(attribute: string) {
        this.parseWhereArguments(true, [attribute, 'IS NOT NULL', null]);
    }

    /**
     * Set order by attribute and direction
     *
     * @param attribute
     * @param direction
     */
    public orderBy(attribute: string, direction: 'asc' | 'desc') {
        this.query.orderBy          = attribute;
        this.query.orderByDirection = direction;
    }

    /**
     * Paginate results
     *
     * @param perPage
     * @param page
     */
    public paginate(perPage: number = 25, page: number = 1) {
        this.query.perPage = perPage;
        this.query.page    = page;
    }

    /**
     * Return the built query
     */
    public getQuery() {
        return Object.assign({}, this.query);
    }

    public reset() {
        this.query = {where: []};
    }

    /**
     * Parse the given where statement to an object in the query
     *
     * @param and
     * @param args
     */
    protected parseWhereArguments(and = true, args: any[]) {

        switch (args.length) {
            // array input
            case 1:
                if (!((args[0] instanceof Array) || (args[0] instanceof Function))) {
                    throw new Error('Given argument must be an array or a callback function');
                }
                const type = args[0] instanceof Array ? 'array' : 'callback';

                if (type === 'callback') {
                    this.processCallbackStatement(args, and);
                } else if (type === 'array') {
                    this.processArrayStatement(args, and);
                }

                break;
            case 2:
                this.addWhereClause('key-operator-value', args[0], '=', args[1], and);
                break;

            case 3:
                this.addWhereClause('key-operator-value', args[0], args[1], args[2], and);
                break;
        }

    }

    /**
     * Add the where clause to the query
     *
     * @param type
     * @param attribute
     * @param operator
     * @param value
     * @param and
     */
    protected addWhereClause(type: WhereTypes, attribute: string, operator: WhereOperators, value: any, and: boolean = true) {
        this.addOrOperator(!and);
        this.addWhereStatement(type, and, attribute, operator, value);
    }

    /**
     * Process arrays with statements
     *
     * @param args
     * @param and
     */
    private processArrayStatement(args: any[], and) {
        for (const where of args[0]) {
            this.parseWhereArguments.call(this, and, where);
        }
    }

    /**
     * Execute callback with QueryBuilder instance
     *
     * @param args
     */
    private processCallbackStatement(args: any[], and: boolean = true) {
        const nestedQuery = new QueryBuilder();

        // execute callback
        args[0].call(null, nestedQuery);

        this.addOrOperator(!and);
        this.query.where = this.query.where.concat([nestedQuery.getQuery().where]);
    }

    /**
     * Add the statement to the query
     *
     * @param type
     * @param and
     * @param attribute
     * @param operator
     * @param value
     */
    protected addWhereStatement(type, and, attribute: string, operator: WhereOperators, value: any) {
        this.query.where = this.query.where.concat([this.createWhereStatement(attribute, operator, value)]);
    }

    /**
     * Create a where statement object
     *
     * @param attribute
     * @param operator
     * @param value
     */
    protected createWhereStatement(attribute: string, operator: WhereOperators, value: number | string) {
        return {
            attr: attribute,
            op: operator,
            val: value
        };
    }

    /**
     * Add an or operator if true
     *
     * @param apply
     */
    protected addOrOperator(apply = true) {
        if (apply) {
            this.query.where = this.query.where.concat(['or']);
        }
    }
}


// WEBPACK FOOTER //
// ./libs/chassis-resource/src/utils/query-builder.ts